import * as React from "react";

// ---------------------------------------------------------------------------
// Quote Cart — a lightweight, localStorage-backed cart for "Add to Order"
// flows. Customers add multiple items (cleaners, supplies, fabric requests…)
// then submit them all in one quote/email request.
// ---------------------------------------------------------------------------

export type QuoteCartItem = {
  /** Unique line-item id (random per add — allows duplicates of same product). */
  id: string;
  /** Stable product id from the catalog. */
  productId: string;
  /** Display name e.g. "303® Fabric Guard™ Non-PFAS". */
  name: string;
  /** Optional size / variant e.g. "32-oz Trigger Sprayer". */
  size?: string;
  /** Optional supplier/source URL for our reference in the email. */
  sourceUrl?: string;
  /** Free-form category tag for grouping in the email (e.g. "Cleaner"). */
  category?: string;
  /** Quantity (defaults to 1). */
  quantity: number;
  /** Optional per-line note. */
  notes?: string;
};

type AddItemInput = Omit<QuoteCartItem, "id" | "quantity"> & {
  quantity?: number;
};

type QuoteCartContextValue = {
  items: QuoteCartItem[];
  count: number;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  addItem: (item: AddItemInput) => void;
  updateQuantity: (id: string, quantity: number) => void;
  updateNotes: (id: string, notes: string) => void;
  removeItem: (id: string) => void;
  clear: () => void;
};

const QuoteCartContext = React.createContext<QuoteCartContextValue | null>(null);

const STORAGE_KEY = "tms_quote_cart_v1";

function loadFromStorage(): QuoteCartItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (i): i is QuoteCartItem =>
        i && typeof i.id === "string" && typeof i.productId === "string" && typeof i.name === "string",
    );
  } catch {
    return [];
  }
}

function saveToStorage(items: QuoteCartItem[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* ignore quota / private mode errors */
  }
}

export function QuoteCartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = React.useState<QuoteCartItem[]>([]);
  const [isOpen, setIsOpen] = React.useState(false);
  const [hydrated, setHydrated] = React.useState(false);

  // Hydrate from localStorage on mount (client only, after SSR)
  React.useEffect(() => {
    setItems(loadFromStorage());
    setHydrated(true);
  }, []);

  // Persist whenever items change (after hydration)
  React.useEffect(() => {
    if (hydrated) saveToStorage(items);
  }, [items, hydrated]);

  const addItem = React.useCallback((input: AddItemInput) => {
    setItems((prev) => {
      // If same productId + same size already exists, just bump quantity
      const existingIdx = prev.findIndex(
        (i) => i.productId === input.productId && (i.size ?? "") === (input.size ?? ""),
      );
      if (existingIdx !== -1) {
        const next = [...prev];
        next[existingIdx] = {
          ...next[existingIdx],
          quantity: next[existingIdx].quantity + (input.quantity ?? 1),
        };
        return next;
      }
      return [
        ...prev,
        {
          id:
            typeof crypto !== "undefined" && "randomUUID" in crypto
              ? crypto.randomUUID()
              : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          productId: input.productId,
          name: input.name,
          size: input.size,
          sourceUrl: input.sourceUrl,
          category: input.category,
          notes: input.notes,
          quantity: Math.max(1, input.quantity ?? 1),
        },
      ];
    });
    setIsOpen(true);
  }, []);

  const updateQuantity = React.useCallback((id: string, quantity: number) => {
    setItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, quantity: Math.max(1, Math.floor(quantity) || 1) } : i)),
    );
  }, []);

  const updateNotes = React.useCallback((id: string, notes: string) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, notes } : i)));
  }, []);

  const removeItem = React.useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const clear = React.useCallback(() => setItems([]), []);
  const open = React.useCallback(() => setIsOpen(true), []);
  const close = React.useCallback(() => setIsOpen(false), []);
  const toggle = React.useCallback(() => setIsOpen((o) => !o), []);

  const count = items.reduce((sum, i) => sum + i.quantity, 0);

  const value = React.useMemo<QuoteCartContextValue>(
    () => ({
      items,
      count,
      isOpen,
      open,
      close,
      toggle,
      addItem,
      updateQuantity,
      updateNotes,
      removeItem,
      clear,
    }),
    [items, count, isOpen, open, close, toggle, addItem, updateQuantity, updateNotes, removeItem, clear],
  );

  return <QuoteCartContext.Provider value={value}>{children}</QuoteCartContext.Provider>;
}

export function useQuoteCart(): QuoteCartContextValue {
  const ctx = React.useContext(QuoteCartContext);
  if (!ctx) {
    throw new Error("useQuoteCart must be used inside <QuoteCartProvider>");
  }
  return ctx;
}
